import { motion } from "framer-motion";
import {
  Sparkles,
  Baby,
  Activity,
} from "lucide-react";

// Custom SVG icons for dental specialties
const ImplantIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="3.5" rx="3" ry="1.5" />
    <path d="M9 5 L9.5 8 L14.5 8 L15 5" />
    <path d="M9.5 8 L10 11 L14 11 L14.5 8" />
    <path d="M10 11 L10.5 14 L13.5 14 L14 11" />
    <path d="M10.5 14 L11 17 L13 17 L13.5 14" />
    <path d="M11 17 L11.5 20 L12.5 20 L13 17" />
    <path d="M11.5 20 L12 22 L12.5 20" />
  </svg>
);

const BracketIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="9" width="5" height="6" rx="0.5" />
    <rect x="16" y="9" width="5" height="6" rx="0.5" />
    <line x1="8" y1="12" x2="16" y2="12" strokeWidth="2" />
    <line x1="5.5" y1="10" x2="5.5" y2="14" strokeWidth="0.5" />
    <line x1="18.5" y1="10" x2="18.5" y2="14" strokeWidth="0.5" />
  </svg>
);

const EndoFileIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="10" y="2" width="4" height="4" rx="0.5" />
    <line x1="12" y1="6" x2="12" y2="22" />
    <path d="M12 8 L14 10 L12 12 L14 14 L12 16 L14 18 L12 20" />
  </svg>
);

const ToothIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 4C5 4 4 6 4 8C4 10 5 11 5 13C5 16 6 20 8 20C9 20 9.5 18 12 18C14.5 18 15 20 16 20C18 20 19 16 19 13C19 11 20 10 20 8C20 6 19 4 17 4C15 4 14 5 12 5C10 5 9 4 7 4Z" />
  </svg>
);

const CrownIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 14L6 6L10 10L12 4L14 10L18 6L20 14" />
    <rect x="4" y="14" width="16" height="6" rx="1" />
  </svg>
);

const Especialidades = () => {
  const specialties = [
    {
      icon: ImplantIcon,
      title: "Implantologia",
      description:
        "Reabilitação oral completa com implantes de última geração para um sorriso natural.",
    },
    {
      icon: BracketIcon,
      title: "Ortodontia",
      description:
        "Aparelhos dentários fixos e invisíveis para alinhar o seu sorriso com conforto.",
    },
    {
      icon: Sparkles,
      title: "Estética Dentária",
      description:
        "Branqueamentos profissionais e facetas dentárias para um sorriso radiante.",
    },
    {
      icon: EndoFileIcon,
      title: "Endodontia",
      description:
        "Tratamento de canais com técnicas avançadas para preservar os seus dentes.",
    },
    {
      icon: Baby,
      title: "Odontopediatria",
      description:
        "Cuidados dentários especializados para crianças num ambiente acolhedor.",
    },
    {
      icon: Activity,
      title: "Periodontologia",
      description:
        "Tratamento de doenças das gengivas para manter a saúde oral completa.",
    },
    {
      icon: ToothIcon,
      title: "Cirurgia Oral",
      description:
        "Procedimentos cirúrgicos minimamente invasivos com recuperação rápida.",
    },
    {
      icon: CrownIcon,
      title: "Prostodontia",
      description:
        "Próteses dentárias fixas e removíveis com alta qualidade e estética.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section
      id="especialidades"
      className="py-16 md:py-24 bg-white"
      data-testid="especialidades-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-xs font-semibold tracking-[0.15em] uppercase text-[#8A8A8A] block mb-4">
            Os Nossos Serviços
          </span>
          <h2
            className="font-outfit text-3xl sm:text-4xl font-semibold text-[#1A1A1A] tracking-tight mb-4"
            data-testid="especialidades-title"
          >
            Especialidades Médico-Dentárias
          </h2>
          <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto">
            Uma equipa multidisciplinar dedicada a cuidar do seu sorriso com as
            técnicas mais avançadas.
          </p>
        </motion.div>

        {/* Specialties Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {specialties.map((specialty, index) => (
            <motion.div
              key={specialty.title}
              variants={itemVariants}
              className="specialty-card bg-[#FAFAFA] border border-gray-100 rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-xl flex flex-col items-start gap-4"
              data-testid={`specialty-card-${specialty.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="bg-white border border-gray-200 w-14 h-14 rounded-xl flex items-center justify-center">
                <specialty.icon className="w-7 h-7 text-[#1A1A1A]" />
              </div>
              <h3 className="font-outfit text-xl font-medium text-[#1A1A1A]">
                {specialty.title}
              </h3>
              <p className="text-[#4A4A4A] text-sm leading-relaxed">
                {specialty.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12"
        >
          <a
            href="tel:232488850"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-8 py-3.5 font-semibold hover:bg-[#333333] transition-all shadow-md"
            data-testid="agendar-consulta-btn"
          >
            <span className="text-white">Agendar Consulta: 232 088 790</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Especialidades;
